import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Zap } from "lucide-react";
import { PublicLayout } from "@/components/PublicLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { apiError, ICE_URL } from "@/lib/api";
import { useAuth, dashboardPath } from "@/context/AuthContext";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const { loginWithToken } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      loginWithToken(data.token, data.user);
      toast.success(`Bienvenue ${data.user.name} !`);
      navigate(dashboardPath(data.user.role));
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/espace-client";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const quickLogin = async (creds) => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", creds);
      loginWithToken(data.token, data.user);
      toast.success(`Bienvenue ${data.user.name} !`);
      navigate(dashboardPath(data.user.role));
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const DEMO = [
    { label: "Admin", email: "admin@climateasy.fr", password: "Admin123!", cls: "bg-[#263871] hover:bg-[#35508F] text-white", testid: "quick-admin" },
    { label: "Technicien", email: "tech@test.fr", password: "Test123!", cls: "bg-emerald-600 hover:bg-emerald-700 text-white", testid: "quick-tech" },
    { label: "Installateur", email: "poseur@test.fr", password: "Test123!", cls: "bg-amber-500 hover:bg-amber-600 text-white", testid: "quick-poseur" },
    { label: "Client", email: "client@test.fr", password: "Test123!", cls: "bg-[#1D9BF0] hover:bg-[#57A4FF] text-white", testid: "quick-client" },
  ];

  return (
    <PublicLayout>
      <section className="relative mx-auto flex min-h-[70vh] max-w-md flex-col justify-center px-4 py-16">
        <img src={ICE_URL} alt="" className="pointer-events-none absolute inset-0 -z-10 h-full w-full rounded-3xl object-cover opacity-10" />
        <h1 className="text-3xl font-extrabold text-[#263871]">Connexion</h1>
        <p className="mt-2 text-slate-600">Heureux de vous revoir sur Climateasy.</p>

        <div className="mt-6 rounded-2xl border border-slate-200 bg-white/70 p-4 backdrop-blur">
          <p className="mb-3 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-500">
            <Zap className="h-3.5 w-3.5 text-[#1D9BF0]" />Connexion rapide (démo)
          </p>
          <div className="grid grid-cols-2 gap-2">
            {DEMO.map((d) => (
              <Button key={d.testid} data-testid={d.testid} type="button" disabled={busy} onClick={() => quickLogin({ email: d.email, password: d.password })} className={`h-11 rounded-xl text-sm font-semibold ${d.cls}`}>
                {d.label}
              </Button>
            ))}
          </div>
        </div>

        <div className="my-5 flex items-center gap-3 text-xs text-slate-400"><span className="h-px flex-1 bg-slate-200" />OU PAR EMAIL<span className="h-px flex-1 bg-slate-200" /></div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 h-12 rounded-xl" />
          </div>
          <div>
            <Label htmlFor="password">Mot de passe</Label>
            <Input id="password" data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 h-12 rounded-xl" />
          </div>
          <Button data-testid="login-submit" type="submit" disabled={busy} className="h-12 w-full rounded-full bg-[#263871] hover:bg-[#35508F]">
            {busy ? "Connexion…" : "Se connecter"}
          </Button>
        </form>
        <div className="my-5 flex items-center gap-3 text-xs text-slate-400"><span className="h-px flex-1 bg-slate-200" />OU<span className="h-px flex-1 bg-slate-200" /></div>
        <Button data-testid="login-google" variant="outline" onClick={google} className="h-12 w-full rounded-full border-2">
          Continuer avec Google
        </Button>
        <p className="mt-6 text-center text-sm text-slate-600">
          Pas encore de compte ? <Link to="/inscription" className="font-semibold text-[#1D9BF0]">Inscription</Link>
        </p>
      </section>
    </PublicLayout>
  );
}
